import { differenceInMinutes, formatDuration } from 'date-fns';
import { sortBy } from 'ramda';
import { getFirstName, getLastName, joinWithOxfordComma } from '../text/text.js';
import { isTransportTask } from './claims.js';
/** User-id order, as the server and migration list people */
const inIdOrder = (people) => sortBy(({ id }) => id ?? 0, people || []);
/** What each placeholder says, before the event's name. */
export const PLACEHOLDER_TEXT = {
    'Check-in Attendees': {
        name: 'Assign Check-In Staff for',
        description: 'Assign Check-In Staff for',
    },
    Teach: { name: 'Assign Teachers for', description: 'Assign Teachers for' },
    DJ: { name: "Assign DJ's for", description: "Assign DJ's or Musicians for" },
    'Coordinate Contest': {
        name: 'Assign Coordinator for',
        description: 'Assign Contest Coordinator for',
    },
    Judge: { name: 'Add Judge to', description: 'Assign Judge for' },
    'Judge Leaders': {
        name: 'Add Leader Judges to',
        description: 'Assign Leader Judges for',
    },
    'Judge Followers': {
        name: 'Add Follower Judges to',
        description: 'Assign Follower Judges for',
    },
    Setup: { name: 'Add Setup Crew for', description: 'Assign Setup Crew for' },
    Cleanup: {
        name: 'Add Cleanup Crew for',
        description: 'Assign Cleanup Crew for',
    },
};
export const DEFAULT_TASK_NAME = 'Assign Task';
/** Openings only a generator writes: placeholders, unstaffed names
 *  (makeTaskName), unstaffed descriptions (makeTaskDescription) and
 *  the festival task form's old "task for <festival>" prefill. */
const GENERATED_OPENINGS = [
    ...Object.values(PLACEHOLDER_TEXT).flatMap(({ name, description }) => [
        name,
        description,
    ]),
    'Assign Someone to',
    'Assign Driver To',
    'TBD will',
    'task for',
];
/** Did a generator write this, not a person? */
export const isGeneratedTaskText = (text) => {
    const value = text.trim();
    return (value === DEFAULT_TASK_NAME ||
        GENERATED_OPENINGS.some((opening) => value.startsWith(`${opening} `)));
};
/** Keep typed text; refresh any a generator wrote. */
export const keepCustomOrDefault = (submitted, oldDefaults, newDefault) => {
    const value = submitted?.trim();
    return !value ||
        isGeneratedTaskText(value) ||
        oldDefaults.some((old) => old.trim() === value)
        ? newDefault
        : value;
};
export const formatTaskType = (type) => type === 'DJ' ? 'DJ' : type.toLowerCase();
const firstNames = (people) => joinWithOxfordComma(people.map(({ name }) => getFirstName(name)));
/** "Alice, Bob, and Carol" from full names */
export const joinNames = (people) => joinWithOxfordComma(people.map(({ name }) => name));
/** "Bob J.", or just "Cher" when there is no last name */
const withLastInitial = (name) => {
    const lastName = getLastName(name);
    return lastName
        ? `${getFirstName(name)} ${lastName[0]}.`
        : getFirstName(name);
};
/** First names (or full / last-initial), or "unknown" for nobody */
export const concatNames = (people, options) => people.length === 0
    ? 'unknown'
    : options?.addLastName
        ? joinNames(people)
        : options?.addLastInitial
            ? joinWithOxfordComma(people.map(({ name }) => withLastInitial(name)))
            : firstNames(people);
/** "Dropoff At Airport" reads "at Airport": the type's own words */
const airportSuffix = (type) => {
    const rest = type.split(' ').slice(1).join(' ');
    return rest.charAt(0).toLowerCase() + rest.slice(1);
};
export const makeTaskName = ({ type, assignedStaff, watchers, }) => {
    const staff = inIdOrder(assignedStaff);
    const riders = inIdOrder(watchers);
    if (isTransportTask({ type })) {
        const action = type.split(' ')[0];
        const airport = type.includes('Airport');
        const staffText = staff.length === 0 ? 'Assign Driver To' : firstNames(staff);
        // unstaffed reads "Assign Driver To Transport Ann"
        const verb = staff.length === 0 || airport ? '' : 'will ';
        const actionText = `${action} ${riders.length === 0 ? 'Staff' : firstNames(riders)}`;
        const suffix = airport ? ` ${airportSuffix(type)}` : '';
        return `${staffText} ${verb}${actionText}${suffix}`;
    }
    if (staff.length === 0)
        return `Assign Someone to ${formatTaskType(type)}`;
    return `${joinNames(staff)} will ${formatTaskType(type)}`;
};
/** Names a generator gave this task for these staff, old formats included */
export const previousTaskNames = (task, staff) => [
    makeTaskName({ ...task, assignedStaff: staff }),
    // airport dropoffs once read "from Airport"
    ...(task.type === 'Dropoff At Airport'
        ? [
            makeTaskName({ ...task, assignedStaff: staff }).replace(/ at Airport$/, ' from Airport'),
        ]
        : []),
    // private lessons were once created with their own name
    ...(task.type === 'Teach' && staff.length
        ? [`${firstNames(inIdOrder(staff))} will Teach a Private Lesson`]
        : []),
];
export const makeTaskDescription = (staff, taskType, { startTime, endTime, venue, }, event) => {
    const people = staff.length ? firstNames(inIdOrder(staff)) : 'TBD';
    const action = taskType?.toLowerCase();
    const time = formatDuration({
        minutes: differenceInMinutes(endTime, startTime),
    });
    const place = venue ? `at ${venue.name}` : '';
    const eventName = event ? `for ${event.name}` : '';
    return `${people} will ${action} ${eventName} for ${time} ${place}`;
};
/** A task's name and description after its staff change, keeping typed text. */
export const refreshTaskText = (task, staffBefore, staffAfter) => ({
    name: keepCustomOrDefault(task.name, previousTaskNames(task, staffBefore), makeTaskName({ ...task, assignedStaff: staffAfter })),
    description: keepCustomOrDefault(task.description, 
    // the create form only has venueId, so no "at <venue>"
    [task, { ...task, venue: null }].map((at) => makeTaskDescription(staffBefore, task.type, at)), makeTaskDescription(staffAfter, task.type, task)),
});
//# sourceMappingURL=taskText.js.map